//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import { SelectValue } from 'antd/lib/select';

import { PicklistValue, PicklistValueServer } from 'components/inputs/types';
import { FieldDataType } from 'components/types';

import { ParsedFuzzyValue } from '../types';

/**
 * Parse fuzzy value format: "value:number" or just "value"
 * The number can represent either percentage or typo count depending on the option's hasMinMaxTypoConfig
 *
 * @param val - The fuzzy value string to parse
 * @returns Object containing the base value and numeric values (percentage and typoCount both set to same number)
 *
 * @example
 * parseFuzzyValue("fieldName:80") // { value: "fieldName", percentage: 80, typoCount: 80 }
 * parseFuzzyValue("fieldName") // { value: "fieldName", percentage: null, typoCount: null }
 */
export const parseFuzzyValue = (val?: string): ParsedFuzzyValue => {
  if (!val || typeof val !== 'string') {
    return { value: '', percentage: null, typoCount: null };
  }

  const parts = val.split(':');

  // Format: "value:number"
  if (parts.length === 2) {
    const numericValue = parseInt(parts[1], 10);
    if (isNaN(numericValue)) {
      return { value: val, percentage: null, typoCount: null };
    }

    // Return the numeric value - interpretation depends on option's hasMinMaxTypoConfig
    // We return both and let the component decide which to use based on the option
    return { value: parts[0], percentage: numericValue, typoCount: numericValue };
  }

  // Format: "value" (no fuzzy config)
  return { value: val, percentage: null, typoCount: null };
};

/**
 * Format fuzzy value: "value:number"
 * The number represents either percentage or typo count depending on the option's hasMinMaxTypoConfig
 *
 * @param value - The base value
 * @param percentageOrTypoCount - The numeric value (percentage or typo count)
 * @returns Formatted fuzzy value string
 *
 * @example
 * formatFuzzyValue("fieldName", 80) // "fieldName:80"
 */
export const formatFuzzyValue = (value: string, percentageOrTypoCount: number): string => {
  return `${value}:${percentageOrTypoCount}`;
};

/**
 * Get display label for a fuzzy value
 *
 * @param val - The value to get display label for
 * @param options - The list of available options
 * @returns Display label string with fuzzy formatting if applicable
 *
 * @example
 * getDisplayLabel("fieldName:80", options) // "Field Name (80%)" or "Field Name (Max 80 typos)"
 */
export const getDisplayLabel = (val: SelectValue | undefined, options: PicklistValue[]): string => {
  if (!val) return '';

  const valStr = typeof val === 'string' ? val : String(val);
  const { value: baseValue, percentage } = parseFuzzyValue(valStr);
  const option = options.find((opt) => opt.value === baseValue);

  if (!option) return valStr;

  // Check if there's a fuzzy value (percentage and typoCount are the same from parsing)
  if (percentage !== null) {
    // Use option's hasMinMaxTypoConfig to determine display format
    if (option.hasMinMaxTypoConfig) {
      const typoText = percentage === 1 ? 'typo' : 'typos';
      return `${option.label} (Max ${percentage} ${typoText})`;
    } else {
      return `${option.label} (${percentage}%)`;
    }
  }

  return option.label;
};

/**
 * Get data type from picklist value, handling both server and UI side cases
 *
 * @param value - The picklist value
 * @returns The data type or undefined
 */
export const getDataTypeFromValue = (value: PicklistValue | PicklistValueServer): FieldDataType | undefined => {
  if ('datatype' in value) {
    return value.datatype;
  } else if ('dataType' in value) {
    return value.dataType;
  }
  return undefined;
};

/**
 * Check if the option should reset to default value when selected
 *
 * @param currentBaseValue - Currently selected base value
 * @param newOptionValue - New option value being selected
 * @returns True if values are different (should reset), false if same
 */
export const shouldResetToDefault = (currentBaseValue: string, newOptionValue: string | number): boolean => {
  return currentBaseValue !== newOptionValue;
};

/**
 * Get initial value for fuzzy option based on config type
 *
 * @param isTypoConfig - Whether this is a typo configuration
 * @param isSameOption - Whether we're re-selecting the same option
 * @param currentValue - Current percentage or typo count value
 * @returns Initial value to use
 */
export const getInitialFuzzyValue = (
  isTypoConfig: boolean,
  isSameOption: boolean,
  currentValue: number | null
): number => {
  if (isSameOption && currentValue !== null) {
    return currentValue;
  }
  return isTypoConfig ? 1 : 70; // Default: 1 for typo, 70% for percentage
};
